import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { InfoPageLayout } from "@/components/pages/InfoPageLayout";
import { DISCLAIMER_SKB, SUPPORTED_YEARS } from "@/data/holidays";
import {
  webPageSchema, breadcrumbSchema, organizationSchema, ldScripts,
} from "@/lib/seo/schema";

const TITLE = "Tentang Kalender Indonesia - Sumber Data & Metode";
const DESC = "Tentang Kalender Indonesia: sumber data libur SKB 3 Menteri, metode konversi Hijriah, Jawa, dan Bali, serta disclaimer.";

export const Route = createFileRoute("/tentang")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: "Tentang Kalender Indonesia" },
      { property: "og:description", content: "Sumber data, metode konversi penanggalan, dan disclaimer Kalender Indonesia." },
    ],
    links: [{ rel: "canonical", href: "https://hari.co.id/tentang/" }],
    scripts: ldScripts([
      webPageSchema({ url: "/tentang", name: TITLE, description: DESC, type: "AboutPage", locale: "id" }),
      breadcrumbSchema([
        { name: "Beranda", url: "/" },
        { name: "Tentang", url: "/tentang" },
      ]),
      organizationSchema("id"),
    ]),
  }),
  component: AboutPage,
});

function AboutPage() {
  const yearStart = SUPPORTED_YEARS[0];
  const yearEnd = SUPPORTED_YEARS[SUPPORTED_YEARS.length - 1];
  return (
    <SiteShell>
      <InfoPageLayout
        variant="about"
        title="Tentang Kalender Indonesia"
        subtitle="Referensi penanggalan Masehi, Hijriah, Jawa & Bali, lengkap dengan tanggal merah, cuti bersama, dan tools."
        badge="i"
        locale="id"
        breadcrumbs={[{ label: "Beranda", href: "/" }, { label: "Tentang" }]}
        aside={
          <div className="space-y-3">
            <div className="rounded-xl border border-border bg-card p-4 text-sm">
              <h3 className="mb-2 font-semibold">Stat singkat</h3>
              <ul className="space-y-1 text-muted-foreground">
                <li><strong className="text-foreground">{yearEnd - yearStart + 1}</strong> tahun didukung ({yearStart}–{yearEnd})</li>
                <li><strong className="text-foreground">4</strong> sistem kalender</li>
                <li><strong className="text-foreground">PDF</strong> ekspor per bulan</li>
              </ul>
            </div>
            <div className="rounded-xl border border-border bg-card p-4 text-sm">
              <h3 className="mb-2 font-semibold">Halaman lain</h3>
              <ul className="space-y-1.5 text-muted-foreground">
                <li><Link className="hover:text-primary" to="/kontak/">Kontak</Link></li>
                <li><Link className="hover:text-primary" to="/kebijakan-privasi/">Kebijakan Privasi</Link></li>
                <li><Link className="hover:text-primary" to="/syarat-ketentuan/">Syarat & Ketentuan</Link></li>
              </ul>
            </div>
          </div>
        }
      >
        <p>
          <strong>Kalender Indonesia</strong> adalah situs referensi penanggalan
          lengkap untuk pengguna Indonesia. Mendukung kalender Masehi, Hijriah,
          Jawa (pasaran &amp; weton), dan Bali (pawukon), serta menampilkan
          libur nasional, cuti bersama, dan hari penting nasional/internasional.
        </p>

        <h2 id="sumber">Sumber Data Libur</h2>
        <p>
          Data libur nasional dan cuti bersama bersumber dari{" "}
          <strong>SKB 3 Menteri</strong> (Menag, Menaker, Menpan-RB) untuk tahun{" "}
          {SUPPORTED_YEARS[0]}–{SUPPORTED_YEARS[SUPPORTED_YEARS.length - 1]}.
          Tahun yang belum memiliki SKB resmi ditandai sebagai{" "}
          <em>data perkiraan</em> dan akan diperbarui setelah keputusan resmi
          diumumkan.
        </p>
        <p className="rounded-md bg-muted p-4 text-sm">{DISCLAIMER_SKB}</p>

        <h2 id="metode">Metode Konversi Penanggalan</h2>
        <div className="not-prose mt-3 grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-border bg-card p-4">
            <h3 className="font-semibold">Hijriah</h3>
            <p className="mt-1 text-sm text-muted-foreground">Aritmetika sipil (Kuwaiti). Bisa berbeda 1 hari dari rukyat.</p>
          </div>
          <div className="rounded-xl border border-border bg-card p-4">
            <h3 className="font-semibold">Jawa</h3>
            <p className="mt-1 text-sm text-muted-foreground">Sultan Agungan, epoch 1 Sura 1555 J (8 Juli 1633 M) - Jumat Legi.</p>
          </div>
          <div className="rounded-xl border border-border bg-card p-4">
            <h3 className="font-semibold">Bali</h3>
            <p className="mt-1 text-sm text-muted-foreground">Pawukon, siklus 210 hari: saptawara, pancawara, triwara, wuku.</p>
          </div>
        </div>

        <h2 id="disclaimer">Disclaimer</h2>
        <p>
          Data di situs ini bersifat <strong>referensi</strong> dan dapat
          mengandung kesalahan. Untuk keperluan resmi seperti administrasi
          pemerintahan, jadwal ibadah, atau dokumen hukum, mohon verifikasi
          ulang ke sumber resmi terkait.
        </p>

        <h2 id="navigasi">Navigasi</h2>
        <ul>
          <li><Link to="/">Beranda</Link></li>
          <li><Link to="/tools/kalkulator-hari/">Kalkulator Hari</Link></li>
          <li><Link to="/tools/cek-weton/">Cek Weton Jawa</Link></li>
        </ul>
      </InfoPageLayout>
    </SiteShell>
  );
}